import * as React from 'react';
import { createPortal } from 'react-dom';
import { IWithToolbarProps } from './types';
import { ToolbarContext } from './context';
import { Toolbar } from './toolbar';
import { useMicroStore } from '../../hooks';
import { useChooseRef } from '../../utils';
import { IToolbarItemProps } from './variants';

const sortItemsInAscendingOrder = (a: IToolbarItemProps, b: IToolbarItemProps) => ((a.order! - b.order!) || 0);

interface IApplyFilterData {
    itemsWithOrder: IToolbarItemProps[];
    itemsWithoutOrder: IToolbarItemProps[];
}
function useCommandsFilter(commands: IToolbarItemProps[], position: IToolbarItemProps['position']) {
    const applyFilter = React.useCallback((data: IApplyFilterData, item: IToolbarItemProps) => {
        const { hidden, order, position: itemPosition } = item;
        if (hidden || position !== itemPosition) {
            return data;
        }

        data[order === undefined ? 'itemsWithoutOrder' : 'itemsWithOrder'].push(item);
        return data;
    }, [position]);

    return React.useMemo(() => {
        const { itemsWithOrder, itemsWithoutOrder } = commands.reduce(applyFilter, { itemsWithOrder: [], itemsWithoutOrder: [] });
        return itemsWithOrder.sort(sortItemsInAscendingOrder).concat(itemsWithoutOrder);
    }, [applyFilter, commands]);
}

export const WithToolbar = React.memo((props: IWithToolbarProps) => {
    const {
        children, elementRef, remoteContainerRef, className, iconSize = '16', maxPrimaryItems, iconPosition = 'left',
        overflow = 'menu', useSeparator, separatorGap, height
    } = props;
    const ref = useChooseRef(elementRef);
    const { items, remove, upsert, detachStore } = useMicroStore<IToolbarItemProps>();
    const contextValues = React.useMemo(() => ({ remove, upsert }), [remove, upsert]);
    const primaryToolbarItems = useCommandsFilter(items, 'default');
    const farPositionedToolbarItems = useCommandsFilter(items, 'far');

    React.useEffect(() => detachStore, [detachStore]);

    const toolbarProps = React.useMemo(() => ({
        elementRef: ref, className, primaryToolbarItems, farPositionedToolbarItems,
        iconSize, iconPosition, maxPrimaryItems, overflow, useSeparator, separatorGap, height
    }), [ref, className, primaryToolbarItems, farPositionedToolbarItems, iconSize, iconPosition, maxPrimaryItems, overflow, useSeparator, separatorGap, height]);

    const toolbarComponent = React.useMemo(() => {
        const component = React.createElement(Toolbar, toolbarProps);
        if (remoteContainerRef?.current) {
            return createPortal(component, remoteContainerRef.current);
        }
        return component;
    }, [toolbarProps, remoteContainerRef]);

    return (
        <ToolbarContext.Provider value={contextValues}>
            {toolbarComponent}
            {children}
        </ToolbarContext.Provider>
    );
});

WithToolbar.displayName = 'WithToolbar';
